import React, { useState } from 'react';
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useAppVersionCheck } from '../../hooks/use-app-version-check';
import { useTenantBranding } from '../../contexts/tenant-branding-context';

interface UpdateAvailableSnackbarProps {
  // Storybook/demo override — production callers omit it and inherit the
  // hook's NODE_ENV gate. The explicit `= undefined` default below is
  // load-bearing: it satisfies react/require-default-props (CI fails on
  // warnings), and any real default would override the hook's gate.
  enabled?: boolean;
}

export const UpdateAvailableSnackbar: React.FC<UpdateAvailableSnackbarProps> = ({ enabled = undefined }) => {
  const { t } = useTranslation('components', { keyPrefix: 'update-available-snackbar' });
  const { brand } = useTenantBranding();
  const { availableHash } = useAppVersionCheck({ enabled });
  const [ dismissedHash, setDismissedHash ] = useState<string | null>(null);

  const open = availableHash !== null && availableHash !== dismissedHash;

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setDismissedHash(availableHash);
  };

  const handleRefreshClick = () => {
    window.location.reload();
  };

  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      // Deviation from the house 3000/6000ms default: an update notice must
      // not vanish before the user has a chance to act on it.
      autoHideDuration={null}
      open={open}
      onClose={handleClose}
    >
      <Alert
        action={(
          <>
            <Button
              color='inherit'
              size='small'
              onClick={handleRefreshClick}
            >
              {t('refresh_button')}
            </Button>
            <IconButton
              aria-label='close'
              color='inherit'
              size='small'
              onClick={() => handleClose()}
            >
              <CloseIcon fontSize='small' />
            </IconButton>
          </>
        )}
        severity='info'
        sx={{
          width: '100%',
          // MUI's action slot defaults to flex-start + top padding, which leaves
          // the Refresh/close buttons sitting higher than the message text.
          '& .MuiAlert-action': {
            alignItems: 'center',
            pt: 0,
          },
        }}
        variant='filled'
      >
        {t('update_available_message', { brandName: brand.brandName })}
      </Alert>
    </Snackbar>
  );
};
